import React, { useState, useCallback, useEffect } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../lib/utils';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastState {
  id: number;
  message: string;
  variant: ToastVariant;
}

const VARIANT_STYLES: Record<ToastVariant, { bg: string; text: string; icon: React.ReactNode }> = {
  success: {
    bg:   'bg-green-600',
    text: 'text-white',
    icon: <CheckCircle size={18} />,
  },
  error: {
    bg:   'bg-error-red',
    text: 'text-white',
    icon: <AlertCircle size={18} />,
  },
  warning: {
    bg:   'bg-amber-500',
    text: 'text-white',
    icon: <AlertTriangle size={18} />,
  },
  info: {
    bg:   'bg-gray-800',
    text: 'text-white',
    icon: <Info size={18} />,
  },
};

// ── Individual Toast pill ─────────────────────────────────────────────────────

function ToastPill({ toast, onDismiss }: { toast: ToastState; onDismiss: (id: number) => void }) {
  const style = VARIANT_STYLES[toast.variant];

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 4000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 px-4 py-3 rounded-2xl shadow-lg max-w-sm w-full',
        'animate-in slide-in-from-bottom-4 fade-in duration-200',
        style.bg,
        style.text
      )}
    >
      <span className="flex-shrink-0 mt-0.5">{style.icon}</span>
      <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity ml-1 mt-0.5"
        aria-label="Dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

let _counter = 0;

/**
 * `useToast` — lightweight in-app snackbar system.
 *
 * Usage:
 *   const { showToast, ToastMount } = useToast();
 *
 *   showToast('Saved!', 'success');
 *   showToast('Please fill all fields.', 'warning');
 *
 *   return <div>...content...<ToastMount /></div>;
 */
export function useToast() {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const showToast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = ++_counter;
    setToasts(prev => [...prev, { id, message, variant }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  /**
   * Render this once somewhere in your component's JSX tree —
   * it will float above all content at the bottom-centre of the screen.
   */
  const ToastMount = () => (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[9998] flex flex-col gap-2 items-center pointer-events-none w-full px-4"
      aria-live="polite"
    >
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto w-full max-w-sm">
          <ToastPill toast={toast} onDismiss={dismiss} />
        </div>
      ))}
    </div>
  );

  return { showToast, ToastMount };
}
